/**
 * deliveredSize — what the export lane will actually ship for a crop.
 *
 * Mirrors archivist's `_resolve_ladder_target` + `_resize_for_export`
 * (src/archivist/worker.py) EXACTLY; the sizing itself happens only there,
 * this is a display-side prediction so the editor can see the delivered
 * output size next to a crop. Keep the two in sync — the Python side is
 * the authority.
 *
 * Semantics:
 * - Ladder mode (non-empty `ladder`) REPLACES the box: sizing is decided on
 *   the long side alone, ranges are half-open [from, to) with the last rule
 *   open-ended (to: null), `target: null` means "ship untouched", and the
 *   save-time validator guarantees the ladder never downscales.
 * - Box mode (empty ladder, per-orientation bounding box): Lightroom-style
 *   contain fit; landscape means width >= height; a missing box for the
 *   orientation ships untouched; sources larger than the box DO downscale;
 *   smaller sources upscale only when `allowUpscale`.
 * - Neither configured: ships untouched.
 *
 * Rounding: JS Math.round vs Python's banker's rounding can differ by 1px
 * on exact .5 products — irrelevant for a display readout.
 *
 * @param {number} cropW crop width in SOURCE pixels
 * @param {number} cropH crop height in SOURCE pixels
 * @param {object} [config] company output config (from the auth payload)
 * @param {Array}  [config.output_long_side_ladder]
 * @param {number} [config.output_landscape_w]
 * @param {number} [config.output_landscape_h]
 * @param {number} [config.output_portrait_w]
 * @param {number} [config.output_portrait_h]
 * @param {boolean} [config.output_allow_upscale]
 * @returns {{w: number, h: number, resized: boolean, upscaled: boolean} | null}
 *   Delivered dimensions, or null when the inputs are unusable.
 */
export function deliveredSize(cropW, cropH, config) {
  if (!cropW || !cropH || cropW <= 0 || cropH <= 0) return null;
  const passthrough = { w: cropW, h: cropH, resized: false, upscaled: false };
  if (!config) return passthrough;

  const ladder = config.output_long_side_ladder || [];
  let scale = null;

  if (ladder.length > 0) {
    const longSide = Math.max(cropW, cropH);
    const target = resolveLadderTarget(longSide, ladder);
    // undefined = malformed ladder (no rule matched). Archivist fails loudly
    // there; a display readout just declines to predict.
    if (target === undefined) return null;
    if (target === null) return passthrough;
    scale = target / longSide;
  } else {
    const landscape = cropW >= cropH;
    const boxW = landscape ? config.output_landscape_w : config.output_portrait_w;
    const boxH = landscape ? config.output_landscape_h : config.output_portrait_h;
    if (!boxW || !boxH) return passthrough;
    scale = Math.min(boxW / cropW, boxH / cropH);
    if (scale >= 1.0 && !config.output_allow_upscale) return passthrough;
  }

  return {
    w: Math.max(1, Math.round(cropW * scale)),
    h: Math.max(1, Math.round(cropH * scale)),
    resized: true,
    upscaled: scale > 1.0,
  };
}

/**
 * The ladder lookup, half-open [from, to). Returns the matched rule's
 * target (number or null = untouched), or undefined when no rule matches
 * (malformed ladder — validated server-side, so this means stale data).
 */
export function resolveLadderTarget(longSide, ladder) {
  for (const rule of ladder) {
    const lo = rule.from;
    const hi = rule.to;
    if (typeof lo !== 'number' || longSide < lo) continue;
    if (hi === null || hi === undefined || longSide < hi) return rule.target ?? null;
  }
  return undefined;
}

/**
 * Compact readout string: "4 812 × 3 208 → delivers 3 543 px".
 * Untouched crops read "ships at source size" — still an answer, not silence.
 */
export function formatDeliveredSize(cropW, cropH, config) {
  const out = deliveredSize(cropW, cropH, config);
  if (!out) return '';
  const src = `${cropW} × ${cropH}`;
  if (!out.resized) return `${src} px · ships at source size`;
  return `${src} → delivers ${Math.max(out.w, out.h)} px${out.upscaled ? ' (upscaled)' : ''}`;
}
